import type React from 'react';
import { colors } from '../theme/colors';
import { calculateProgressState, getProgressColor } from '../utils/progressUtils';

export interface CircularProgressContentProps {
  /** Current value */
  value: number;
  /** Maximum value (e.g., FTE hours) */
  max: number;
  /** Display format for the value */
  format?: 'percentage' | 'hours' | 'both';
  /** Custom label text */
  label?: string;
  /** Custom sublabel text */
  sublabel?: string;
  /** Color scheme for different progress states */
  colorScheme?: {
    under?: string;
    near?: string;
    overtime?: string;
    default?: string;
  };
  /** Thresholds for color changes (as percentages of max) */
  thresholds?: {
    near?: number;
    overtime?: number;
  };
  /** Custom className for styling */
  className?: string;
  /** Custom style object */
  style?: React.CSSProperties;
}

export const CircularProgressContent: React.FC<CircularProgressContentProps> = ({
  value,
  max,
  format = 'percentage',
  colorScheme = {
    under: colors.accent.yellow,
    near: colors.accent.green,
    overtime: colors.accent.blue,
    default: colors.accent.green,
  },
  thresholds = {
    near: 0.9,
    overtime: 1.0,
  },
  className,
  style,
}) => {
  // Calculate progress state using centralized utility
  const progressState = calculateProgressState(value, max, thresholds);
  const textColor = getProgressColor(progressState, colorScheme);

  // Format the display value
  const formatValue = () => {
    switch (format) {
      case 'hours':
        return `${value.toFixed(1)}h`;
      case 'both':
        return `${Math.round(progressState.progress * 100)}% (${value.toFixed(1)}h)`;
      default:
        return `${Math.round(progressState.progress * 100)}%`;
    }
  };

  return (
    <div
      className={className}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        ...style,
      }}
    >
      <div
        style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: textColor,
        }}
      >
        {value && max > 0 ? formatValue() : '--'}
      </div>
      <div
        style={{
          fontSize: '0.7rem',
          color: colors.secondary.text,
          marginTop: '0.5rem',
          minWidth: '4.5rem',
        }}
      >
        <span style={{ height: '1rem' }}>Actual Hours</span>
        <hr style={{ width: '100%', border: `1px solid ${colors.neutral.grey[300]}` }} />
        <span style={{ height: '1rem' }}>Plan Hours</span>
      </div>{' '}
    </div>
  );
};

export default CircularProgressContent;
